// @file Content picker helper.
import { supportsRecording, supportsScreenCapture, supportsVideoRecording } from '@@/bits/av_recorder'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { MAX_PHOTO_ALBUM_ATTACHMENTS } from '@@/bits/post_drafts'
import { SearchFilters } from '@@/bits/web_search'
import { AttachmentTypeId, AttachmentTypeMode } from '@@/types'
import { groupBy } from 'es-toolkit'

interface ContentSource {
  type: ContentPickerSource
  text: string
  ariaLabel: string
  disabledAriaLabel?: string
  icon: string
  activeIcon: string
  color: 'purple' | 'blue' | 'green' | 'orange'
  visible: boolean
}

interface ContentPickerSourceMenuListItem {
  type: string
  text: string
  ariaLabel?: string
  icon?: string
  activeIcon?: string
  color: string
  hiddenInputId?: string
  visible: boolean
  disabled: boolean
  isNew?: boolean
  actionType?: string
  actionPayload?: string | object
  avatarUrl?: string
  avatarAltText?: string
  testId?: string
}

enum ContentPickerSource {
  FileBrowser = 'file',
  LinkInput = 'link',
  WebBrowser = 'web_search',
  PhotoBooth = 'photo_booth',
  VideoRecorder = 'video_recorder',
  AudioRecorder = 'audio_recorder',
  DrawingCanvas = 'drawing',
  PadletBrowser = 'padlet',
  MapBrowser = 'location_search',
  ScreenshareRecorder = 'screen_recorder',
  AiImageSearch = 'ai_image_search',
  ImageSearch = 'image_search',
  ThreeDObjectSearch = 'three_d_object_search',
  StockImageSearch = 'stock_image_search',
  VideoSearch = 'video_search',
  AudioSearch = 'audio_search',
  GifSearch = 'gif_search',
  Poll = 'poll',
  GoogleDrive = 'google_drive',
  MicrosoftOneDrive = 'microsoft_one_drive_picker',
  AiTalk = 'ai_talk',
  MediaLibrary = 'media_library',
  PhotoAlbum = 'photo_album',
  Stickers = 'stickers',
  DeviceUploads = 'device_uploads',
}

type NoSearchFilterError = 'NO_SEARCH_FILTER'

// Image picker types that are allowed in photo album content picker
const ALLOWED_IMAGE_PICKER_SOURCES: ContentPickerSource[] = [
  ContentPickerSource.FileBrowser, // Upload (Desktop)
  ContentPickerSource.MediaLibrary, // Upload (Native App)
  ContentPickerSource.LinkInput, // Link
  ContentPickerSource.PhotoBooth, // Camera
  ContentPickerSource.DrawingCanvas, // Draw
  ContentPickerSource.AiImageSearch, // AI Image
  ContentPickerSource.ImageSearch, // Image Search
  ContentPickerSource.GifSearch, // GIF
]

/**
 * Checks if all image picker types are disabled
 * @param attachmentSettings Object containing attachment settings for each content picker source
 * @returns true if all image picker types are disabled, false otherwise
 */
const areAllImagePickerSourcesDisabledFromSettings = (
  attachmentSettings: Record<ContentPickerSource, boolean> | boolean,
): boolean => {
  if (typeof attachmentSettings === 'boolean') {
    return !attachmentSettings
  }
  return ALLOWED_IMAGE_PICKER_SOURCES.every((source) => !attachmentSettings[source])
}

const ATTACHMENT_TYPE_TO_CONTENT_PICKER_SOURCE_MAP: Partial<Record<AttachmentTypeId, ContentPickerSource>> = {
  [AttachmentTypeId.Upload]: ContentPickerSource.FileBrowser,
  [AttachmentTypeId.Link]: ContentPickerSource.LinkInput,
  [AttachmentTypeId.Camera]: ContentPickerSource.PhotoBooth,
  [AttachmentTypeId.Draw]: ContentPickerSource.DrawingCanvas,
  [AttachmentTypeId.ICantDraw]: ContentPickerSource.AiImageSearch,
  [AttachmentTypeId.ImageSearch]: ContentPickerSource.ImageSearch,
  [AttachmentTypeId.Gif]: ContentPickerSource.GifSearch,
}

/**
 * Checks if all image picker types are disabled based on attachment type states
 * @param attachmentTypeStates Object containing attachment type states
 * @returns true if all image picker types are disabled, false otherwise
 */
const areAllImagePickerSourcesDisabledFromAttachmentTypeStates = (
  attachmentTypeStates: Record<AttachmentTypeId, AttachmentTypeMode>,
): boolean => {
  // Get image picker attachment type IDs that map to ALLOWED_IMAGE_PICKER_SOURCES
  const imagePickerAttachmentTypeIds = Object.keys(ATTACHMENT_TYPE_TO_CONTENT_PICKER_SOURCE_MAP) as AttachmentTypeId[]

  // Check if all image picker attachment types are disabled
  return imagePickerAttachmentTypeIds.every((attachmentTypeId) => {
    return attachmentTypeStates[attachmentTypeId] === AttachmentTypeMode.Disabled
  })
}

// Some content sources like `MediaLibrary` is a mobile-app only content source.
// Technically, we'll never have this content source in the web code but since we set a strict type
// for this constant, we still need to implement it. It's an okay tradeoff to ensure the completeness
// of `CONTENT_PICKER_SOURCE_ARIA_LABEL_MAP_FOR_SETTINGS`.
const CONTENT_PICKER_SOURCE_ARIA_LABEL_MAP_FOR_SETTINGS: Record<ContentPickerSource, string> = {
  [ContentPickerSource.PadletBrowser]: __('Toggle padlet attachment'),
  [ContentPickerSource.FileBrowser]: __('Toggle upload attachment'),
  [ContentPickerSource.LinkInput]: __('Toggle link attachment'),
  [ContentPickerSource.PhotoBooth]: __('Toggle photo attachment'),
  [ContentPickerSource.VideoRecorder]: __('Toggle video recording attachment'),
  [ContentPickerSource.AudioRecorder]: __('Toggle audio recording attachment'),
  [ContentPickerSource.ScreenshareRecorder]: __('Toggle screen recording attachment'),
  [ContentPickerSource.DrawingCanvas]: __('Toggle drawing attachment'),
  [ContentPickerSource.Poll]: __('Toggle poll attachment'),
  [ContentPickerSource.GoogleDrive]: __('Toggle Google Drive attachment'),
  [ContentPickerSource.MicrosoftOneDrive]: __('Toggle Microsoft OneDrive attachment'),
  [ContentPickerSource.AiImageSearch]: __('Toggle AI Art Generator attachment'),
  [ContentPickerSource.AiTalk]: __('Toggle AI Speech Generator attachment'),
  [ContentPickerSource.ImageSearch]: __('Toggle image search'),
  [ContentPickerSource.ThreeDObjectSearch]: __('Toggle 3D object search'),
  [ContentPickerSource.StockImageSearch]: __('Toggle stock image search'),
  [ContentPickerSource.GifSearch]: __('Toggle GIF attachment'),
  [ContentPickerSource.VideoSearch]: __('Toggle video search'),
  [ContentPickerSource.AudioSearch]: __('Toggle audio search'),
  [ContentPickerSource.WebBrowser]: __('Toggle web search'),
  [ContentPickerSource.MapBrowser]: __('Toggle location attachment'),
  [ContentPickerSource.MediaLibrary]: __('Toggle photo and video library'),
  [ContentPickerSource.PhotoAlbum]: __('Toggle photo album attachment'),
  [ContentPickerSource.Stickers]: __('Toggle stickers'),
  [ContentPickerSource.DeviceUploads]: __('Toggle upload from mobile'),
}

// If you update this, do update the schemas in services/rails/app/services/libraries/attachment_type_settings.rb too.
const getContentSources = (context: 'post' | 'whiteboard'): ContentSource[] =>
  [
    {
      type: ContentPickerSource.PadletBrowser,
      text: __('Padlet'),
      ariaLabel: __('Attach another padlet'),
      disabledAriaLabel: __('An admin has disabled the option to attach another padlet'),
      icon: 'padlet_crane',
      activeIcon: 'padlet',
      color: 'purple',
      visible: true,
    },
    {
      type: ContentPickerSource.FileBrowser,
      text: __('Upload'),
      ariaLabel: __('Upload a file'),
      disabledAriaLabel: __('An admin has disabled the option to upload files'),
      icon: 'upload_outline',
      activeIcon: 'upload',
      color: 'purple',
      visible: true,
    },
    {
      type: ContentPickerSource.LinkInput,
      text: __('Link'),
      ariaLabel: __('Attach a link'),
      disabledAriaLabel: __('An admin has disabled the option to attach links'),
      icon: 'link',
      activeIcon: 'link_filled',
      color: 'purple',
      visible: true,
    },
    {
      type: ContentPickerSource.MediaLibrary,
      text: __('Photo and Video Library'),
      ariaLabel: __('Upload from photo library'),
      disabledAriaLabel: __('An admin has disabled the option to upload from photo library'),
      icon: 'photo_video_outline',
      activeIcon: 'photo_video',
      color: 'blue',
      visible: device.app,
    },
    {
      type: ContentPickerSource.PhotoBooth,
      text: __('Camera'),
      ariaLabel: __('Take a photo'),
      disabledAriaLabel: __('An admin has disabled the option to take a photo'),
      icon: 'camera_outline',
      activeIcon: 'camera',
      color: 'blue',
      visible: true,
    },
    {
      type: ContentPickerSource.VideoRecorder,
      text: __('Video recorder'),
      ariaLabel: __('Record a video'),
      disabledAriaLabel: __('An admin has disabled the option to record a video'),
      icon: 'video_outline',
      activeIcon: 'video',
      color: 'blue',
      visible: supportsVideoRecording || device.mobile,
    },
    {
      type: ContentPickerSource.AudioRecorder,
      text: __('Audio recorder'),
      ariaLabel: __('Record an audio'),
      disabledAriaLabel: __('An admin has disabled the option to record an audio'),
      icon: 'microphone_outline',
      activeIcon: 'microphone',
      color: 'blue',
      visible: supportsRecording,
    },
    {
      type: ContentPickerSource.ScreenshareRecorder,
      text: __('Screen recorder'),
      ariaLabel: __('Record your screen'),
      disabledAriaLabel: __('An admin has disabled the option to record your screen'),
      icon: 'screen_recorder_outline',
      activeIcon: 'screen_recorder',
      color: 'blue',
      visible: supportsScreenCapture,
    },
    {
      type: ContentPickerSource.DrawingCanvas,
      text: __('Draw'),
      ariaLabel: __('Draw something'),
      disabledAriaLabel: __('An admin has disabled the option to draw'),
      icon: 'draw_outline',
      activeIcon: 'draw',
      color: 'blue',
      visible: context === 'post',
    },
    {
      type: ContentPickerSource.Poll,
      text: __('Poll'),
      ariaLabel: __('Make a poll'),
      disabledAriaLabel: __('An admin has disabled the option to make a poll'),
      icon: 'poll_outline',
      activeIcon: 'poll',
      color: 'green',
      visible: true,
    },
    {
      type: ContentPickerSource.GoogleDrive,
      text: __('Google Drive'),
      ariaLabel: __('Upload from Google Drive'),
      disabledAriaLabel: __('An admin has disabled the option to upload from Google Drive'),
      icon: 'google_drive_outline',
      activeIcon: 'google_drive_fill',
      color: 'green',
      visible: !device.app,
    },
    {
      type: ContentPickerSource.MicrosoftOneDrive,
      text: __('OneDrive'),
      ariaLabel: __('Upload from Microsoft OneDrive'),
      disabledAriaLabel: __('An admin has disabled the option to upload from Microsoft OneDrive'),
      icon: 'microsoft_onedrive_outline',
      activeIcon: 'microsoft_onedrive_fill',
      color: 'green',
      visible: isAppUsing('microsoftOneDrivePicker') && !device.app,
    },
    {
      type: ContentPickerSource.AiImageSearch,
      text: __('AI image'),
      ariaLabel: __('Use AI Art Generator'),
      disabledAriaLabel: __('An admin has disabled the option to use AI Art Generator'),
      icon: 'ai_images',
      activeIcon: 'ai_images_filled',
      color: 'green',
      visible: isAppUsing('aiImageSearch'),
    },
    {
      type: ContentPickerSource.AiTalk,
      text: __('AI speech'),
      ariaLabel: __('Use AI Speech Generator'),
      disabledAriaLabel: __('An admin has disabled the option to use AI Speech Generator'),
      icon: 'communicate',
      activeIcon: 'communicate_filled',
      color: 'green',
      visible: !device.app,
    },
    {
      type: ContentPickerSource.PhotoAlbum,
      text: __('Photo album'),
      ariaLabel: __('Create a photo album containing up to %{count} images', { count: MAX_PHOTO_ALBUM_ATTACHMENTS }),
      disabledAriaLabel: __('An admin has disabled the option to pick multiple photos'),
      icon: 'photo_video_outline',
      activeIcon: 'photo_video',
      color: 'green',
      visible: context === 'post',
    },
    {
      type: ContentPickerSource.DeviceUploads,
      text: __('From mobile'),
      ariaLabel: __('Upload from mobile device'),
      disabledAriaLabel: __('An admin has disabled the option to upload from mobile'),
      icon: 'upload_from_mobile',
      activeIcon: 'upload_from_mobile',
      color: 'green',
      visible: isAppUsing('deviceUploads') && context !== 'whiteboard' && !device.app && !device.mobile,
    },
    {
      type: ContentPickerSource.ImageSearch,
      text: __('Image search'),
      ariaLabel: __('Search for an image'),
      disabledAriaLabel: __('An admin has disabled the option to search for an image'),
      icon: 'image_search_outline',
      activeIcon: 'image_search',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.Stickers,
      text: __('Stickers'),
      ariaLabel: __('Add a sticker'),
      disabledAriaLabel: __('An admin has disabled the option to add a sticker'),
      icon: 'stickers',
      activeIcon: 'stickers_filled',
      color: 'orange',
      visible: context === 'whiteboard',
    },
    {
      type: ContentPickerSource.GifSearch,
      text: __('GIF'),
      ariaLabel: __('Search for a GIF'),
      disabledAriaLabel: __('An admin has disabled the option to search for a GIF'),
      icon: 'gifs',
      activeIcon: 'gif',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.VideoSearch,
      text: __('YouTube'),
      ariaLabel: __('Search on YouTube'),
      disabledAriaLabel: __('An admin has disabled the option to search on YouTube'),
      icon: 'youtube_outline',
      activeIcon: 'youtube',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.AudioSearch,
      text: __('Spotify'),
      ariaLabel: __('Search on Spotify'),
      disabledAriaLabel: __('An admin has disabled the option to search on Spotify'),
      icon: 'spotify_outline',
      activeIcon: 'spotify',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.WebBrowser,
      text: __('Web search'),
      ariaLabel: __('Search on the web'),
      disabledAriaLabel: __('An admin has disabled the option to search on the web'),
      icon: 'web_search_outline',
      activeIcon: 'web_search',
      color: 'orange',
      visible: true,
    },
    {
      type: ContentPickerSource.MapBrowser,
      text: __('Location'),
      ariaLabel: __('Add a location'),
      disabledAriaLabel: __('An admin has disabled the option to add a location'),
      icon: 'location_outline',
      activeIcon: 'location',
      color: 'orange',
      visible: true,
    },
  ].filter((source) => source.visible) as ContentSource[]

const getContentSourcesByColor = (): Record<string, ContentSource[]> =>
  groupBy(getContentSources('post'), (source) => source.color)

const getWhiteboardContentSources = (): ContentSource[] => {
  return getContentSources('whiteboard')
}

const SEARCH_MODE_BY_CONTENT_SOURCE = {
  [ContentPickerSource.ImageSearch]: SearchFilters.Images,
  [ContentPickerSource.GifSearch]: SearchFilters.Gif,
  [ContentPickerSource.VideoSearch]: SearchFilters.Videos,
  [ContentPickerSource.AudioSearch]: SearchFilters.Audio,
  [ContentPickerSource.WebBrowser]: SearchFilters.Web,
} as const

const CONTENT_SOURCE_BY_SEARCH_MODE = {
  [SearchFilters.Images]: ContentPickerSource.ImageSearch,
  [SearchFilters.Gif]: ContentPickerSource.GifSearch,
  [SearchFilters.Videos]: ContentPickerSource.VideoSearch,
  [SearchFilters.Audio]: ContentPickerSource.AudioSearch,
  [SearchFilters.Web]: ContentPickerSource.WebBrowser,
  [SearchFilters.AiImages]: ContentPickerSource.AiImageSearch,
  [SearchFilters.Stickers]: ContentPickerSource.Stickers,
} as const

const MEDIA_SEARCH_CONTENT_SOURCES = [
  ContentPickerSource.ImageSearch,
  ContentPickerSource.GifSearch,
  ContentPickerSource.VideoSearch,
] as const

const MEDIA_SEARCH_MODES = MEDIA_SEARCH_CONTENT_SOURCES.map((source) => SEARCH_MODE_BY_CONTENT_SOURCE[source])

const DEVICE_UPLOADABLE_CONTENT_SOURCES: ContentPickerSource[] = [
  ContentPickerSource.FileBrowser,
  ContentPickerSource.PhotoBooth,
  ContentPickerSource.VideoRecorder,
  ContentPickerSource.AudioRecorder,
  ContentPickerSource.DrawingCanvas,
  ContentPickerSource.PhotoAlbum,
]

const getAiSearchSamplePrompt = (): string =>
  'An expressive oil painting of a basketball player dunking, as depicted as an explosion of a nebula.'
const getTranslatedAiSearchSamplePrompt = (): string =>
  __('An expressive oil painting of a basketball player dunking, as depicted as an explosion of a nebula')

export {
  ALLOWED_IMAGE_PICKER_SOURCES,
  areAllImagePickerSourcesDisabledFromAttachmentTypeStates,
  areAllImagePickerSourcesDisabledFromSettings,
  CONTENT_PICKER_SOURCE_ARIA_LABEL_MAP_FOR_SETTINGS,
  CONTENT_SOURCE_BY_SEARCH_MODE,
  ContentPickerSource,
  DEVICE_UPLOADABLE_CONTENT_SOURCES,
  getAiSearchSamplePrompt,
  getContentSources,
  getContentSourcesByColor,
  getTranslatedAiSearchSamplePrompt,
  getWhiteboardContentSources,
  MEDIA_SEARCH_CONTENT_SOURCES,
  MEDIA_SEARCH_MODES,
  SEARCH_MODE_BY_CONTENT_SOURCE,
}
export type { ContentPickerSourceMenuListItem, ContentSource, NoSearchFilterError }
